import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import About from './pages/About';
import Contact from './pages/Contact';
import ProfilePage from './pages/ProfilePage';
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SearchPage from './pages/SearchPage';
import DownloadPage from './pages/DownloadPage';
import FriendRequestsPage from './pages/FriendRequestsPage';
import FollowersPage from './pages/FollowersPage';
import FollowingPage from './pages/FollowingPage';
import FriendsPage from './pages/FriendsPage';
import BlockedUsersPage from './pages/BlockedUsersPage';
import ReportsPage from './pages/ReportsPage';
import { GroupsPage } from './pages/GroupsPage';
import { GroupPage } from './pages/GroupPage';
import MessagingPage from './pages/MessagingPage';
import ConversationPage from './pages/ConversationPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { ToastProvider } from './components/ui/ToastProvider';
import { PostDetailPage } from './pages/PostDetailPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MessageNotificationContainer } from './components/MessageNotificationContainer';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminGroupsPage } from './pages/admin/AdminGroupsPage';
import { AdminPostsPage } from './pages/admin/AdminPostsPage';
import { AdminReportsPage } from './pages/admin/AdminReportsPage';
import { AdminLogsPage } from './pages/admin/AdminLogsPage';
import { AdminApkVersionsPage } from './pages/admin/AdminApkVersionsPage';
import { AdminBadgesPage } from './pages/admin/AdminBadgesPage';
import { AdminModerationPage } from './pages/admin/AdminModerationPage';
import { AdminProtectedRoute } from './components/admin';
import { useAuth } from './hooks/useAuth';
import { webSocketManager } from './services/webSocketManager';
import VideoCallPage from './pages/VideoCallPage';
import VideoCallDemoPage from './pages/VideoCallDemoPage';
import './App.css';

function AppContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize WebSocket connection once when user is authenticated
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('token');
      if (token && !webSocketManager.getIsConnected()) {
        webSocketManager.connect(token);
      }
    }

    // Cleanup: Disconnect when app unmounts
    return () => {
      webSocketManager.disconnect();
    };
  }, [user]);

  const handleOpenConversation = (conversationId: number) => {
    navigate(`/messages/${conversationId}`);
  };

  // Check if we're on the messaging pages
  const isMessagingPage = location.pathname.startsWith('/messages') || location.pathname.startsWith('/messaging');

  return (
    <>
      <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/:userId" element={<ProfilePage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/download" element={<DownloadPage />} />
        
        <Route path="/posts/:postId" element={<PostDetailPage />} />
        
        <Route path="/friend-requests" element={<FriendRequestsPage />} />
        <Route path="/followers" element={<FollowersPage />} />
        <Route path="/followers/:userId" element={<FollowersPage />} />
        <Route path="/following" element={<FollowingPage />} />
        <Route path="/following/:userId" element={<FollowingPage />} />
        <Route path="/friends" element={<FriendsPage />} />
        <Route path="/friends/:userId" element={<FriendsPage />} />
        <Route path="/blocked-users" element={<BlockedUsersPage />} />
        <Route path="/reports" element={<ReportsPage />} />

        <Route path="/notifications" element={<NotificationsPage />} />

        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/groups/:groupId" element={<GroupPage />} />

        {/* Video Call Routes */}
        <Route path="/call/video/:conversationId" element={<VideoCallPage />} />
        <Route path="/call/voice/:conversationId" element={<VideoCallPage />} />
        <Route path="/call/:callId" element={<VideoCallPage />} />
        <Route path="/demo/videocall" element={<VideoCallDemoPage />} />

        {/* Messaging Routes */}
        <Route path="/messages" element={<MessagingPage />} />
        <Route path="/messages/:conversationId" element={<ConversationPage />} />
        
        {/* Backward compatibility - redirect old messaging route */}
        <Route path="/messaging" element={<Navigate to="/messages" replace />} />
        <Route path="/messaging/:conversationId" element={<Navigate to="/messages/:conversationId" replace />} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route
          path="/admin/dashboard"
          element={
            <AdminProtectedRoute>
              <AdminDashboardPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminProtectedRoute>
              <AdminUsersPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/groups"
          element={
            <AdminProtectedRoute>
              <AdminGroupsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/posts"
          element={
            <AdminProtectedRoute>
              <AdminPostsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <AdminProtectedRoute>
              <AdminReportsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/logs"
          element={
            <AdminProtectedRoute>
              <AdminLogsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/apk-versions"
          element={
            <AdminProtectedRoute>
              <AdminApkVersionsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/badges"
          element={
            <AdminProtectedRoute>
              <AdminBadgesPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/moderation"
          element={
            <AdminProtectedRoute>
              <AdminModerationPage />
            </AdminProtectedRoute>
          }
        />
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      </Routes>

      {/* Global Components - Only show when user is logged in */}
      {user && (
        <>
          <MessageNotificationContainer onOpenConversation={handleOpenConversation} />
        </>
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <AppContent />
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
